import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { BUS_MAINTENANCE_STAY_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import {
  clearBusMaintenanceStay,
  getBusMaintenanceStay,
  putBusMaintenanceStay,
} from '../../../../actions/mechanical-maintenance/BusMaintenanceStay';
import BusMaintenanceStayForm from './BusMaintenanceStayForm';
import { tzNormalizeDate } from '../../../../utils/date';
import { TIMESTAMP_FORMAT } from '../../../../config/locale';
import { BUS_MAINTENANCE_STATUS_TYPE } from '../../../../config/constants';

const EditBusMaintenanceStay = ({
  dispatchClearBusMaintenanceStay,
  dispatchGetBusMaintenanceStay,
  dispatchPutBusMaintenanceStay,
  loading,
  busMaintenanceStay,
  busMaintenanceStayId,
}) => {
  useLayoutEffect(() => {
    dispatchGetBusMaintenanceStay({ busMaintenanceStayId });

    return () => dispatchClearBusMaintenanceStay();
  }, []);

  const onSubmit = (formValues) => {
    const newFormValues = { ...formValues };

    newFormValues.registeredBusId = formValues.registeredBusId.value;
    newFormValues.originCityId = formValues.originCityId.value;

    newFormValues.estimatedArrivalDate = busMaintenanceStay.get(
      'estimatedArrivalDate',
    );

    newFormValues.maintenanceStayStatus =
      formValues.maintenanceStayStatus.value;

    const completionDate = formValues.estimatedCompletionDate;
    const completionTime = formValues.estimatedCompletionTime;
    if (completionDate && completionTime) {
      const estimatedCompletionDateTime = new Date(
        completionDate.getFullYear(),
        completionDate.getMonth(),
        completionDate.getDate(),
        completionTime.getHours(),
        completionTime.getMinutes(),
        completionTime.getSeconds() || 0,
      );
      newFormValues.estimatedCompletionDate = tzNormalizeDate({
        date: estimatedCompletionDateTime,
        format: TIMESTAMP_FORMAT,
      });
    }

    dispatchPutBusMaintenanceStay(busMaintenanceStayId, newFormValues);
  };

  const generateInitialValues = ({
    registeredBusId,
    registeredBus,
    originCityId,
    originCity,
    maintenanceStayStatus,
    observation,
    estimatedArrivalDate,
    estimatedCompletionDate,
  }) => ({
    registeredBusId: registeredBusId && {
      value: registeredBusId,
      label: registeredBus.companyBusId,
    },
    originCityId: originCityId && {
      value: originCityId,
      label: originCity.name,
    },
    maintenanceStayStatus: BUS_MAINTENANCE_STATUS_TYPE[maintenanceStayStatus],
    observation,
    estimatedArrivalDate: estimatedArrivalDate
      ? tzNormalizeDate({ date: estimatedArrivalDate }).slice(0, 16)
      : null,

    estimatedCompletionDate: estimatedCompletionDate
      ? tzNormalizeDate({ date: estimatedCompletionDate }).slice(0, 16)
      : null,
  });

  let content;

  if (loading) content = <Loader />;
  else if (busMaintenanceStay.isEmpty())
    content = <NoDataResource returnPage={BUS_MAINTENANCE_STAY_PATH} />;
  else
    content = (
      <BusMaintenanceStayForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(busMaintenanceStay.toJS())}
        edit
      />
    );

  return content;
};

EditBusMaintenanceStay.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearBusMaintenanceStay: PropTypes.func.isRequired,
  dispatchGetBusMaintenanceStay: PropTypes.func.isRequired,
  dispatchPutBusMaintenanceStay: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  busMaintenanceStay: PropTypes.instanceOf(Immutable.Map).isRequired,
  busMaintenanceStayId: PropTypes.number,
};

EditBusMaintenanceStay.defaultProps = {
  loading: false,
  busMaintenanceStayId: 0,
};

const mapDispatchToProps = {
  dispatchClearBusMaintenanceStay: clearBusMaintenanceStay,
  dispatchGetBusMaintenanceStay: getBusMaintenanceStay,
  dispatchPutBusMaintenanceStay: putBusMaintenanceStay,
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => ({
  busMaintenanceStay: MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'current',
    'content',
  ]),
  loading: !MechanicalMaintenanceUnit.BusMaintenanceStay.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditBusMaintenanceStay);
